import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { blogsService } from '../services/dataService';
import { ArrowLeft, Calendar, Clock, Tag, Share2 } from 'lucide-react';
import { format } from 'date-fns';
import 'react-quill/dist/quill.snow.css';

const BlogPost = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  const handleShare = async () => {
  const shareData = {
    title: blog.title,
    text: blog.description || blog.title,
    url: window.location.href
  };

  try {
    
    if (navigator.share) {
      await navigator.share(shareData);
    } else {
      
      await navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  } catch (err) {
    console.error('Error sharing:', err);
  }
};


  useEffect(() => {
    loadBlog();
  }, [slug]);

  const loadBlog = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await blogsService.getAll();

      if (result.success) {
        const foundBlog = result.data.find(b => b.slug === slug && b.published);

        if (foundBlog) {
          setBlog(foundBlog);
        } else {
          setError('Blog post not found or not published');
        }
      } else {
        setError('Failed to load blog post');
      }
    } catch (err) {
      setError('Error loading blog post');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white font-mono flex items-center justify-center px-4">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin mb-4"></div>
          <p className="text-white/60">Loading post...</p>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen bg-black text-white font-mono">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <button
            onClick={() => navigate('/writings')}
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Writings</span>
          </button>

          <div className="p-6 sm:p-8 rounded-lg border border-red-500/20 bg-red-500/5 text-center">
            <p className="text-red-400 mb-4 text-sm sm:text-base">{error || 'Blog post not found'}</p>
            <button
              onClick={() => navigate('/writings')}
              className="text-white/60 hover:text-white transition-colors text-sm"
            >
              View all posts
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white font-mono">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Navigation */}
        <nav className="mb-6 sm:mb-8">
          <button
            onClick={() => navigate('/writings')}
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Writings</span>
          </button>
        </nav>

        {/* Article Header */}
        <article>
          <header className="sticky top-0 bg-black z-10 pb-4 mb-6 sm:mb-8">
            {/* {blog.featured && (
      <span className="inline-block mb-3 text-xs px-3 py-1 rounded bg-yellow-500/20 text-yellow-400">
        Featured Post
      </span>
    )} */}

            <h1 className="text-2xl sm:text-3xl md:text-4xl font-light mb-4 leading-tight">
              {blog.title}
            </h1>

            {blog.description && (
              <p className="text-base sm:text-lg mb-4 leading-relaxed blog-description-text">
                {blog.description}
              </p>
            )}

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-white/40 pb-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(blog.date)}</span>
              </div>

              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{blog.readTime}</span>
              </div>

              {blog.tags && blog.tags.length > 0 && (
                <div className="flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  <span className="truncate max-w-[200px] sm:max-w-none">{blog.tags.join(', ')}</span>
                </div>
              )}

              <button
                onClick={handleShare}
                className="ml-auto flex items-center gap-2 text-white/60 hover:text-white transition-colors"
              >
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">Share</span>
              </button>
            </div>
          </header>
          {/* Blog Content */}

          <div
            className="blog-content prose prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />
          {/* Tags */}
          {blog.tags && blog.tags.length > 0 && (
            <div className="mt-6 sm:mt-8 pt-6 border-t border-white/10">
              <h3 className="text-sm text-white/40 mb-3">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {blog.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 rounded bg-white/10 text-white/60 text-xs sm:text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </article>
      </div>

      {/* Blog Content Styles */}
      <style>{`
  .blog-description-text {
    color: rgba(255, 255, 255, 0.6) !important;
  }

  .blog-content,
  .blog-content p,
  .blog-content span,
  .blog-content div,
  .blog-content * {
    color: rgba(255, 255, 255, 0.8) !important;
  }

  .blog-content {
    font-size: 0.875rem;
    line-height: 1.7;
  }

  @media (min-width: 640px) {
    .blog-content {
      font-size: 1rem;
    }
  }
  
  .blog-content h1 {
    color: white !important;
    font-size: 1.5rem;
    font-weight: 300;
    margin: 1.5rem 0 0.75rem;
    line-height: 1.3;
  }

  @media (min-width: 640px) {
    .blog-content h1 {
      font-size: 1.75rem;
    }
  }
  
  .blog-content h2 {
    color: white !important;
    font-size: 1.25rem;
    font-weight: 300;
    margin: 1.25rem 0 0.75rem;
    line-height: 1.4;
  }

  @media (min-width: 640px) {
    .blog-content h2 {
      font-size: 1.375rem;
    }
  }
  
  .blog-content h3 {
    color: white !important;
    font-size: 1rem;
    font-weight: 400;
    margin: 1rem 0 0.5rem;
    line-height: 1.4;
  }

  @media (min-width: 640px) {
    .blog-content h3 {
      font-size: 1.125rem;
    }
  }
  
  .blog-content p {
    margin: 1rem 0;
  }
  
  .blog-content a {
    color: #60a5fa !important;
    text-decoration: underline;
    text-decoration-color: rgba(96, 165, 250, 0.3);
    transition: all 0.2s;
    word-break: break-word;
  }
  
  .blog-content a:hover {
    color: #93c5fd !important;
    text-decoration-color: rgba(147, 197, 253, 0.5);
  }
  
  .blog-content strong {
    color: white !important;
    font-weight: 600;
  }
  
  .blog-content code {
    background: rgba(255, 255, 255, 0.1);
    padding: 0.2em 0.4em;
    border-radius: 0.25rem;
    font-size: 0.85em;
    font-family: 'Courier New', monospace;
    word-break: break-all;
  }
  
  .blog-content pre {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 0.5rem;
    padding: 0.75rem;
    overflow-x: auto;
    margin: 1.25rem 0;
  }

  @media (min-width: 640px) {
    .blog-content pre {
      padding: 1rem;
    }
  }
  
  .blog-content pre code {
    background: none;
    padding: 0;
    font-size: 0.75rem;
    line-height: 1.6;
  }

  @media (min-width: 640px) {
    .blog-content pre code {
      font-size: 0.875rem;
    }
  }
  
  .blog-content blockquote {
    border-left: 3px solid rgba(255, 255, 255, 0.2);
    padding-left: 0.75rem;
    margin: 1.25rem 0;
    color: rgba(255, 255, 255, 0.6) !important;
    font-style: italic;
  }

  @media (min-width: 640px) {
    .blog-content blockquote {
      padding-left: 1rem;
    }
  }
  
  .blog-content ul,
  .blog-content ol {
    margin: 1rem 0;
    padding-left: 1.5rem;
  }

  @media (min-width: 640px) {
    .blog-content ul,
    .blog-content ol {
      padding-left: 1.75rem;
    }
  }
  
  .blog-content li {
    margin: 0.4rem 0;
  }
  
  .blog-content img {
    max-width: 100%;
    height: auto;
    border-radius: 0.5rem;
    margin: 1.5rem 0;
  }
  
  .blog-content hr {
    border: none;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    margin: 1.5rem 0;
  }

  .blog-content table {
    width: 100%;
    overflow-x: auto;
    display: block;
    border-collapse: collapse;
  }

  @media (min-width: 640px) {
    .blog-content table {
      display: table;
    }
  }
`}</style>
    </div>
  );
};

export default BlogPost;